import { call, put, takeLatest, select, all } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { client } from "@/core/apollo-client";
import { ORDER_MUTATIONS, ORDER_QUERIES, GET_ORDERS_LIST } from "./mutations";
import {
  getAppState,
  sendIframeMessage,
  createOrderDataForIframe,
  createErrorDataForIframe,
} from "./utils";
import {
  // Cart operations
  removeOrderItem,
  updateQuantity,

  // Order lifecycle
  createOrderStart,
  createOrderSuccess,
  createOrderError,

  // Order updates
  updateOrderStart,
  updateOrderSuccess,
  updateOrderError,

  // Order loading
  loadOrdersStart,
  loadOrdersSuccess,
  loadOrdersError,
  loadOrderStart,
  loadOrderSuccess,
  loadOrderError,

  // Order history
  loadOrderHistoryStart,
  loadOrderHistorySuccess,
  loadOrderHistoryError,
  setOrderFilters,
  loadMoreOrders,

  // General state
  setCartLoading,
  setCartError,
} from "./reducer";
import { CreateOrderFromCartInput, OrderFilters } from "./types";
import {
  OrderCreateInput,
  OrderUpdateInput,
  OrderInput,
  OrdersInput,
  OrdersFilterInput,
  OrderStatus,
  OrderType,
} from "@/types/generated/graphql-types";
import { RootState } from "@/core/store";

// ===== UTILITY FUNCTIONS =====

// Convert cart items to GraphQL order input
function* buildOrderInputFromCart(
  cartInput: CreateOrderFromCartInput
): Generator<any, OrderCreateInput, any> {
  const state: RootState = yield select();
  const cartItems = state.orders.currentOrder.items;
  const comment = state.orders.currentOrder.comment;

  const itemsAdd = cartItems.map((item) => ({
    productId: item.dish.id,
    quantity: item.qty,
  }));

  return {
    brandId: cartInput.brandId,
    pointId: cartInput.pointId,
    type: cartInput.type,
    comment: cartInput.comment || comment || undefined,
    personsNumber: cartInput.personsNumber,
    itemsAdd,
  };
}

// Error handling utility
function getErrorMessage(error: any, defaultMessage: string): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error?.graphQLErrors?.length > 0) {
    return error.graphQLErrors[0].message;
  }
  if (error?.networkError?.message) {
    return error.networkError.message;
  }
  return defaultMessage;
}

// Convert local OrderFilters to GraphQL OrdersFilterInput
function convertOrderFiltersToGraphQL(
  filters: OrderFilters
): OrdersFilterInput {
  return {
    // Convert string[] to OrderStatus[] if provided
    statuses: filters.statuses?.map((status) => status as OrderStatus),
    // Convert string[] to OrderType[] if provided
    types: filters.types?.map((type) => type as OrderType),
    // pointsId expects UUID array
    pointsId: filters.pointIds,
    // Note: dateFrom/dateTo are not part of GraphQL OrdersFilterInput
    // These would need to be handled at a different level or in a different filter
  };
}

// ===== ORDER LIFECYCLE SAGAS =====

/**
 * Dispatcher saga that routes order creation based on app.source
 * Implements the conditional flow design from creative phase
 */
function* createOrderDispatcherSaga(
  action: PayloadAction<CreateOrderFromCartInput>
): Generator<any, void, any> {
  try {
    yield put(setCartLoading(true));
    yield put(setCartError(null));

    // Get app state to determine flow type
    const appState = yield call(getAppState);

    if (appState?.source === "preorder") {
      // Route to preorder flow
      yield call(createPreorderSaga, action);
    } else {
      // Route to normal order flow
      yield call(createNormalOrderSaga, action);
    }
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка создания заказа");
    yield put(createOrderError(errorMessage));
    yield put(setCartError(errorMessage));
  } finally {
    yield put(setCartLoading(false));
  }
}

/**
 * Preorder flow saga - handles iframe communication
 */
function* createPreorderSaga(
  action: PayloadAction<CreateOrderFromCartInput>
): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { currentOrder } = state.orders;
    const appState = yield call(getAppState);

    if (!appState) {
      throw new Error("App state not available for preorder");
    }

    // Create mock order for iframe data (preorder doesn't create real order)
    const mockOrder = {
      id: `preorder_${Date.now()}`,
      type: action.payload.type,
    };

    // Create order data for iframe
    const orderData = createOrderDataForIframe(
      currentOrder.items,
      mockOrder,
      appState,
      currentOrder.comment || ""
    );

    // Send iframe message to parent window
    sendIframeMessage("PREORDER_COMPLETED", appState.source, orderData);

    // Dispatch success action (will clear cart)
    yield put(createOrderSuccess(mockOrder as any));
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка создания предзаказа");

    // Create error data for iframe
    const state: RootState = yield select();
    const { currentOrder } = state.orders;
    const errorData = createErrorDataForIframe(
      errorMessage,
      currentOrder.items.length,
      currentOrder.sum
    );

    // Send error message to iframe
    const appState = getAppState();
    if (appState?.source) {
      sendIframeMessage("PREORDER_ERROR", appState.source, errorData);
    }

    throw error; // Re-throw to be handled by dispatcher
  }
}

/**
 * Normal order flow saga - handles GraphQL order creation
 */
function* createNormalOrderSaga(
  action: PayloadAction<CreateOrderFromCartInput>
): Generator<any, void, any> {
  try {
    // Build order input from cart
    const orderInput: OrderCreateInput = yield call(
      buildOrderInputFromCart,
      action.payload
    );

    // Create order via GraphQL
    const response = yield call([client, client.mutate], {
      mutation: ORDER_MUTATIONS.CREATE_ORDER,
      variables: {
        input: orderInput,
      },
    });

    const order = response.data.orderCreate;
    yield put(createOrderSuccess(order));
  } catch (error) {
    throw error; // Re-throw to be handled by dispatcher
  }
}

// Maintain compatibility - createOrderSaga now points to dispatcher
const createOrderSaga = createOrderDispatcherSaga;

function* updateOrderSaga(
  action: PayloadAction<{ orderId: string; updates: Partial<OrderUpdateInput> }>
): Generator<any, void, any> {
  try {
    yield put(updateOrderStart());

    const { orderId, updates } = action.payload;
    const updateInput: OrderUpdateInput = {
      id: orderId,
      brandId: updates.brandId!,
      ...updates,
    };

    const response = yield call([client, client.mutate], {
      mutation: ORDER_MUTATIONS.UPDATE_ORDER,
      variables: {
        input: updateInput,
      },
    });

    const order = response.data.orderUpdate;
    yield put(updateOrderSuccess(order));
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка обновления заказа");
    yield put(updateOrderError(errorMessage));
  }
}

// ===== ORDER LOADING SAGAS =====

function* loadOrderSaga(
  action: PayloadAction<OrderInput>
): Generator<any, void, any> {
  try {
    yield put(loadOrderStart());

    const response = yield call([client, client.query], {
      query: ORDER_QUERIES.GET_ORDER,
      variables: {
        input: action.payload,
      },
      fetchPolicy: "cache-first",
    });

    const order = response.data.order;
    yield put(loadOrderSuccess(order));
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка загрузки заказа");
    yield put(loadOrderError(errorMessage));
  }
}

function* loadOrdersSaga(
  action: PayloadAction<OrdersInput>
): Generator<any, void, any> {
  try {
    yield put(loadOrdersStart());

    const response = yield call([client, client.query], {
      query: ORDER_QUERIES.GET_ORDERS,
      variables: {
        input: action.payload,
      },
      fetchPolicy: "cache-first",
    });

    const orders = response.data.orders;
    yield put(loadOrdersSuccess(orders));
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка загрузки заказов");
    yield put(loadOrdersError(errorMessage));
  }
}

// ===== ORDER HISTORY SAGAS =====

function* loadOrderHistorySaga(
  action: PayloadAction<{
    ordersInput: OrdersInput;
    append?: boolean;
    pageSize?: number;
  }>
): Generator<any, void, any> {
  try {
    yield put(loadOrderHistoryStart());

    const { ordersInput, append = false, pageSize = 20 } = action.payload;

    const response = yield call([client, client.query], {
      query: GET_ORDERS_LIST,
      variables: {
        input: ordersInput,
      },
      fetchPolicy: "cache-first",
    });

    const orders = response.data.orders;
    const hasMore = orders.length >= pageSize;

    yield put(
      loadOrderHistorySuccess({
        orders,
        hasMore,
        append,
      })
    );
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      "Ошибка загрузки истории заказов"
    );
    yield put(loadOrderHistoryError(errorMessage));
  }
}

function* loadMoreOrdersSaga(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { orderHistory } = state.orders;

    // Build input with current filters and offset
    const ordersInput: OrdersInput = {
      brandId: "current_brand_id", // TODO: Get from app state/context
      filter: convertOrderFiltersToGraphQL(orderHistory.filters),
    };

    yield call(loadOrderHistorySaga, {
      type: loadOrderHistoryStart.type,
      payload: {
        ordersInput,
        append: true,
      },
    });
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      "Ошибка загрузки дополнительных заказов"
    );
    yield put(loadOrderHistoryError(errorMessage));
  }
}

function* setOrderFiltersSaga(
  action: PayloadAction<OrderFilters>
): Generator<any, void, any> {
  try {
    // Update filters in state
    yield put(setOrderFilters(action.payload));

    // Reload history with new filters
    const ordersInput: OrdersInput = {
      brandId: "current_brand_id", // TODO: Get from app state/context
      filter: convertOrderFiltersToGraphQL(action.payload),
    };

    yield call(loadOrderHistorySaga, {
      type: loadOrderHistoryStart.type,
      payload: {
        ordersInput,
        append: false,
      },
    });
  } catch (error) {
    const errorMessage = getErrorMessage(error, "Ошибка применения фильтров");
    yield put(loadOrderHistoryError(errorMessage));
  }
}

// ===== OPTIMISTIC UPDATE SAGAS =====

function* optimisticUpdateQuantitySaga(
  action: PayloadAction<{ itemUUID: string; quantity: number }>
): Generator<any, void, any> {
  // Optimistic update is already handled in reducer
  // This saga can be used for additional side effects if needed
  try {
    // Could sync with backend if needed
    // For now, just local state update via reducer is sufficient
    yield put(updateQuantity(action.payload));
  } catch (error) {
    // Handle rollback if needed
    console.error("Optimistic update failed:", error);
  }
}

function* optimisticRemoveItemSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  // Optimistic update is already handled in reducer
  try {
    yield put(removeOrderItem(action.payload));
  } catch (error) {
    console.error("Optimistic remove failed:", error);
  }
}

// ===== ROOT SAGA =====

export function* ordersSaga() {
  yield all([
    // Order lifecycle
    takeLatest(createOrderStart.type, createOrderSaga),
    takeLatest(updateOrderStart.type, updateOrderSaga),

    // Order loading
    takeLatest(loadOrderStart.type, loadOrderSaga),
    takeLatest(loadOrdersStart.type, loadOrdersSaga),

    // Order history
    takeLatest(loadOrderHistoryStart.type, loadOrderHistorySaga),
    takeLatest(loadMoreOrders.type, loadMoreOrdersSaga),
    takeLatest(setOrderFilters.type, setOrderFiltersSaga),

    // Optimistic updates
    takeLatest(updateQuantity.type, optimisticUpdateQuantitySaga),
    takeLatest(removeOrderItem.type, optimisticRemoveItemSaga),

    // Additional sagas can be added here
  ]);
}
